/**
 * Video Setup
 *
 * Initializes video picker components and shares handler instances by post.
 */

import { VideoUtils } from "./video-utils";
import { VideoListener } from "./video-listener";
import { VideoHandler } from "./video-handler";
import { videoForm } from "./video-form";
import { PostMediaRender } from "../../../post/media/post-media-render";
import type { VideoThumbnailData } from "./video-render";

interface SelectedVideoItem {
    file: File;
    previewUrl: string;
    name?: string;
}

const videoHandlerRegistry = new Map<string, VideoHandler>();

function toPostMediaKey(postKey: string | number): string {
    return String(postKey);
}

function registerVideoHandler(postKey: string, handler: VideoHandler): void {
    videoHandlerRegistry.set(toPostMediaKey(postKey), handler);
}

function getVideoHandler(postKey: string): VideoHandler | undefined {
    return videoHandlerRegistry.get(toPostMediaKey(postKey));
}

function getVideoHandlerByPostId(postId: number): VideoHandler | undefined {
    return videoHandlerRegistry.get(toPostMediaKey(postId));
}

class VideoSetup {
    public static createSelectedVideosSlot(): SelectedVideoItem[] {
        return [];
    }

    public static initializeEditMode(postId: number, root: HTMLElement | null = null): void {
        const postKey = toPostMediaKey(postId);
        const handler = new VideoHandler(postKey, "edit");

        const container = root || videoForm.getContainer(postKey, document);
        if (!(container instanceof HTMLElement)) return;

        const fileInput = videoForm.getFileInput(container);
        const addBtn = videoForm.getAddButton(container);
        const grid = videoForm.getVideoGrid(container);

        if (!(fileInput instanceof HTMLInputElement)) return;
        if (!(addBtn instanceof HTMLButtonElement)) return;
        if (!(grid instanceof HTMLElement)) return;

        handler.container = container;
        handler.fileInput = fileInput;
        handler.addBtn = addBtn;
        handler.videoGrid = grid;

        let selected = VideoSetup.createSelectedVideosSlot();

        const render = (): void => {
            grid.innerHTML = "";
            selected.forEach((video, index) => {
                const tile = PostMediaRender.createVideoElement(video, index);
                tile.dataset.videoType = "new";

                const removeBtn = videoForm.getTileRemoveButton(tile);
                if (removeBtn) {
                    VideoListener.setupTileRemoveButtonListener(removeBtn, (event: Event) => {
                        event.preventDefault();
                        const idx = Number(removeBtn.dataset.index);
                        if (!Number.isFinite(idx) || idx < 0 || idx >= selected.length) return;
                        selected.splice(idx, 1);

                        const dt = new DataTransfer();
                        selected.forEach((item) => dt.items.add(item.file));
                        fileInput.files = dt.files;

                        render();
                    });
                }

                grid.appendChild(tile);
            });
        };

        VideoListener.setupAddButtonListener(addBtn, fileInput);
        VideoListener.setupEditModeClickListeners(handler);

        VideoListener.setupFileInputListener(fileInput, async (files: File[]) => {
            if (files.length === 0) return;

            const dedupeKey = (file: File): string => `${file.name}::${file.size}`;
            const existingKeys = new Set(selected.map((item) => dedupeKey(item.file)));
            const merged = [...selected.map((item) => item.file)];
            files.forEach((file) => {
                if (!file || !file.type.startsWith("video/")) return;
                const key = dedupeKey(file);
                if (existingKeys.has(key)) return;
                merged.push(file);
                existingKeys.add(key);
            });

            const maxFiles = Number(container.dataset.maxFiles || 3);
            const limited = merged.slice(0, Number.isFinite(maxFiles) && maxFiles > 0 ? maxFiles : 3);

            selected = [];
            for (const file of limited) {
                try {
                    const videoData: VideoThumbnailData = await VideoUtils.generateThumbnail(file);
                    selected.push({ file, previewUrl: videoData.previewUrl, name: file.name });
                } catch (_error) {
                    // Thumbnail generation failed (e.g. Android); add video without thumbnail.
                    selected.push({ file, previewUrl: "", name: file.name });
                }
            }

            const dt = new DataTransfer();
            selected.forEach((item) => dt.items.add(item.file));
            fileInput.files = dt.files;

            render();
        });

        render();
        registerVideoHandler(postKey, handler);
    }
}

export { VideoSetup, getVideoHandler, getVideoHandlerByPostId, registerVideoHandler };
